import { useCallback, useMemo, useRef } from 'react';
import { NAVIGATION_CHANNEL, UI_EVENT_TYPE } from './index';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { useUnifiedAnalyticsContext } from '../../providers/unified-analytics-data-provider';

export const UNIFIED_INSTRUMENTATION_SOURCE = 'topNav';
type GROUP_IDENTIFIER = '1pApp' | '3pApp' | 'discoverMore' | 'customLink';

export const GROUP_INDEX_MAP: Record<GROUP_IDENTIFIER, number> = {
	'1pApp': 0,
	'3pApp': 1,
	discoverMore: 2,
	customLink: 3,
};

export const getGroupIndex = (
	identifier: GROUP_IDENTIFIER,
	hasDiscoverySection: boolean = false,
): number => {
	const baseIndex = GROUP_INDEX_MAP[identifier];
	// If no discovery section and is customLink group, shift up one position
	if (identifier === 'customLink' && !hasDiscoverySection) {
		return baseIndex - 1;
	}
	return baseIndex;
};

interface MenuStateChangedAttributes {
	itemState: 'expanded' | 'collapsed';
}

interface MenuViewedAttributes {
	currentOrg: string | undefined;
	customLinkItemCount: number;
	firstPartyInMoreItemCount: number;
	firstPartyInMoreItemList: string[];
	firstPartyItemCount: number;
	firstPartyItemList: string[];
	firstPartyShowMoreButton: boolean;
	hasDiscoverySection: boolean;
	isOrderCustomized: boolean;
	orgCount: number | undefined;
	orgList: (string | undefined)[];
	thirdPartyItemCount: number;
	thirdPartyItemList: (string | undefined)[];
}

interface OrgDropdownViewedAttributes {
	currentOrg: string;
	expandOrgCount: number;
	expandOrgList: string[];
	isDefaultOrg: boolean;
}

interface OrgDropdownClickedAttributes {
	currentOrg: string;
	isDefaultOrg: boolean;
}

interface OrgListItemClickedAttributes {
	currentOrg: string;
	expandOrgCount: number;
	expandOrgId: string;
	expandOrgIndex: number;
	isDefaultOrg: boolean;
}

interface ItemClickedAttributes {
	currentOrg: string;
	groupIdentifier: GROUP_IDENTIFIER;
	groupIndex: number;
	groupItemCount: number;
	groupItemIndex: number;
	groupItemName: string;
	groupItemProduct: string;
	groupItemSite: string;
	isDefaultOrg: boolean;
	isInMore: boolean;
}

interface UnifiedAnalyticsEvents {
	// Switcher item clicked event when a switcher item is clicked (1P, 3P, customLink)
	fireItemClicked: (attributes: ItemClickedAttributes) => void;
	// Switcher expanded or collapsed event via switcher button or navigation actions
	fireMenuStateChanged: (attributes: MenuStateChangedAttributes) => void;
	// Switcher menu viewed event when switcher is opened
	fireMenuViewed: (attributes: MenuViewedAttributes) => void;
	// Organisation dropdown (top selector) clicked
	fireOrgDropdownClicked: (attributes: OrgDropdownClickedAttributes) => void;
	// Organisation dropdown viewed (when the dropdown list is rendered)
	fireOrgDropdownViewed: (attributes: OrgDropdownViewedAttributes) => void;
	// Organisation list item clicked inside the expanded dropdown
	fireOrgListItemClicked: (attributes: OrgListItemClickedAttributes) => void;
}

export function useUnifiedAnalytics(): UnifiedAnalyticsEvents {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { productSwitcher, unifiedInstrumentationEnabled } = useUnifiedAnalyticsContext();

	const baseAttributes = useRef({
		navLayer: 'global',
		navContainer: 'top',
		navVersion: '4',
	});

	const topLevelAttributes = useMemo(
		() => ({
			actionSubject: 'atlassianSwitcher',
			eventType: 'ui',
			source: 'topNav',
			product: productSwitcher,
			tags: ['navigationExperiences'],
		}),
		[productSwitcher],
	);

	// Data portal link: https://data-portal.internal.atlassian.com/analytics/registry/91979
	const fireMenuStateChanged = useCallback(
		(attributes: MenuStateChangedAttributes) => {
			createAnalyticsEvent({
				...topLevelAttributes,
				eventType: UI_EVENT_TYPE,
				action: 'changed',
				actionSubjectId: 'atlassianSwitcherMenuState',
				attributes: {
					...baseAttributes.current,
					...attributes,
				},
			}).fire(NAVIGATION_CHANNEL);
		},
		[createAnalyticsEvent, baseAttributes, topLevelAttributes],
	);

	// Data portal link: https://data-portal.internal.atlassian.com/analytics/registry/92277
	const fireOrgDropdownClicked = useCallback(
		(attributes: OrgDropdownClickedAttributes) => {
			if (!unifiedInstrumentationEnabled) {
				return;
			}
			createAnalyticsEvent({
				...topLevelAttributes,
				eventType: UI_EVENT_TYPE,
				action: 'clicked',
				actionSubjectId: 'atlassianSwitcherOrgExpandItem',
				attributes: {
					...baseAttributes.current,
					...attributes,
				},
			}).fire(NAVIGATION_CHANNEL);
		},
		[createAnalyticsEvent, baseAttributes, topLevelAttributes, unifiedInstrumentationEnabled],
	);

	// Data portal link: https://data-portal.internal.atlassian.com/analytics/registry/92276
	const fireOrgDropdownViewed = useCallback(
		(attributes: OrgDropdownViewedAttributes) => {
			if (!unifiedInstrumentationEnabled) {
				return;
			}
			createAnalyticsEvent({
				...topLevelAttributes,
				eventType: UI_EVENT_TYPE,
				action: 'viewed',
				actionSubjectId: 'atlassianSwitcherOrgDropdownViewed',
				attributes: {
					...baseAttributes.current,
					...attributes,
				},
			}).fire(NAVIGATION_CHANNEL);
		},
		[createAnalyticsEvent, baseAttributes, topLevelAttributes, unifiedInstrumentationEnabled],
	);

	// Data portal link: https://data-portal.internal.atlassian.com/analytics/registry/92214
	const fireOrgListItemClicked = useCallback(
		(attributes: OrgListItemClickedAttributes) => {
			if (!unifiedInstrumentationEnabled) {
				return;
			}
			createAnalyticsEvent({
				...topLevelAttributes,
				eventType: UI_EVENT_TYPE,
				action: 'clicked',
				actionSubjectId: 'atlassianSwitcherOrgExpandItem',
				attributes: {
					...baseAttributes.current,
					...attributes,
				},
			}).fire(NAVIGATION_CHANNEL);
		},
		[createAnalyticsEvent, baseAttributes, topLevelAttributes, unifiedInstrumentationEnabled],
	);

	// Data portal link: https://data-portal.internal.atlassian.com/analytics/registry/92268
	const fireMenuViewed = useCallback(
		(attributes: MenuViewedAttributes) => {
			createAnalyticsEvent({
				...topLevelAttributes,
				eventType: UI_EVENT_TYPE,
				action: 'viewed',
				actionSubjectId: 'atlassianSwitcherMenu',
				attributes: {
					...baseAttributes.current,
					isOrderCustomized: attributes.isOrderCustomized,
					currentOrg: attributes.currentOrg,
					isDefaultOrg: true,
					orgCount: attributes.orgCount,
					orgList: attributes.orgList,
					groupList: [
						{
							groupIdentifier: '1pApp',
							groupIndex: getGroupIndex('1pApp'),
							groupItemCount: attributes.firstPartyItemCount,
							groupItemList: attributes.firstPartyItemList,
							showMoreButton: attributes.firstPartyShowMoreButton,
							inMoreItemCount: attributes.firstPartyInMoreItemCount,
							inMoreItemList: attributes.firstPartyInMoreItemList,
						},
						{
							groupIdentifier: '3pApp',
							groupIndex: getGroupIndex('3pApp'),
							groupItemCount: attributes.thirdPartyItemCount,
							groupItemList: attributes.thirdPartyItemList,
							showMoreButton: false,
							inMoreItemCount: 0,
							inMoreItemList: [],
						},
						...(attributes.hasDiscoverySection
							? [
									{
										groupIdentifier: 'discoverMore',
										groupIndex: getGroupIndex('discoverMore'),
										groupItemCount: 1,
										groupItemList: ['moreAtlassianApps'],
										showMoreButton: false,
										inMoreItemCount: 0,
										inMoreItemList: [],
									},
								]
							: []),
						{
							groupIdentifier: 'customLink',
							groupIndex: getGroupIndex('customLink', attributes.hasDiscoverySection),
							groupItemCount: attributes.customLinkItemCount,
							showMoreButton: false,
							inMoreItemCount: 0,
						},
					],
				},
			}).fire(NAVIGATION_CHANNEL);
		},
		[createAnalyticsEvent, topLevelAttributes],
	);

	// Data portal link: https://data-portal.internal.atlassian.com/analytics/registry/92441
	const fireItemClicked = useCallback(
		(attributes: ItemClickedAttributes) => {
			if (!unifiedInstrumentationEnabled) {
				return;
			}
			createAnalyticsEvent({
				...topLevelAttributes,
				eventType: UI_EVENT_TYPE,
				action: 'clicked',
				actionSubjectId: 'atlassianSwitcherItem',
				attributes: {
					...baseAttributes.current,
					...attributes,
				},
			}).fire(NAVIGATION_CHANNEL);
		},
		[createAnalyticsEvent, topLevelAttributes, unifiedInstrumentationEnabled],
	);

	return {
		fireItemClicked,
		fireMenuStateChanged,
		fireMenuViewed,
		fireOrgDropdownClicked,
		fireOrgDropdownViewed,
		fireOrgListItemClicked,
	};
}
